import { MenuItemNode } from './MenuItem';

/**
 * True when the location equals the path or is nested below it
 * (`budget/scenarios/5` matches `budget/scenarios`). Both sides must already
 * be normalized (leading slash stripped). An empty path never matches.
 */
export function matchesMenuPath(normalizedLocation: string, normalizedPath: string): boolean {
    if (!normalizedPath) return false;
    return normalizedLocation === normalizedPath || normalizedLocation.startsWith(`${normalizedPath}/`);
}

/**
 * Recursively locate the menu item whose path matches the given pathname.
 * Detail routes without a menu entry of their own (e.g. `/budget/scenarios/5`)
 * resolve to their closest ancestor entry: among all prefix matches the
 * longest path wins, so `/budget/scenarios/5` maps to Scenarios rather than
 * the `/budget` overview. Returns the matched leaf item plus the trail of its
 * parent groups (accumulated innermost-first). Path comparison strips a
 * leading slash and treats the empty path as `dashboard`, matching the
 * sidebar's own logic.
 */
export function findMenuItem(
    menuItems: MenuItemNode[],
    pathname: string | undefined,
    parents: MenuItemNode[] = []
): [MenuItemNode | undefined, MenuItemNode[]] {
    if (!menuItems) return [undefined, parents];
    const normalizedLocation = (pathname || '').replace(/^\//, '') || 'dashboard';
    let bestItem: MenuItemNode | undefined;
    let bestParents = parents;
    let bestLength = -1;
    for (const item of menuItems) {
        const normalizedPath = 'path' in item ? (item.path || '').replace(/^\//, '') : '';
        if (matchesMenuPath(normalizedLocation, normalizedPath) && normalizedPath.length > bestLength) {
            bestItem = item;
            bestParents = [...parents];
            bestLength = normalizedPath.length;
        }
        if ('nestedItems' in item) {
            const [nestedItem, nestedParents] = findMenuItem(item.nestedItems, pathname, [...parents]);
            if (nestedItem && 'path' in nestedItem) {
                const nestedPath = (nestedItem.path || '').replace(/^\//, '');
                if (nestedPath.length > bestLength) {
                    nestedParents.push(item);
                    bestItem = nestedItem;
                    bestParents = nestedParents;
                    bestLength = nestedPath.length;
                }
            }
        }
    }
    return [bestItem, bestParents];
}

/**
 * Build a breadcrumb-style title for the active route, innermost page first,
 * e.g. `Offered / Bidding`. `parents` is already ordered innermost-first, so
 * the leaf simply leads. A repeated label is collapsed when a group and its
 * leaf share a name (e.g. the Forecasts group's list page → `Forecasts`, not
 * `Forecasts / Forecasts`). Returns undefined when no menu item matches.
 *
 * `getLabel` resolves each item's display label; it defaults to the raw
 * `item.text` source, and callers pass a translator (t → `navigation` locale)
 * to build a localized title.
 */
export function buildMenuTitle(
    menuItems: MenuItemNode[],
    pathname: string | undefined,
    getLabel: (item: MenuItemNode) => string = (item) => item.text ?? ''
): string | undefined {
    const [leaf, parents] = findMenuItem(menuItems, pathname);
    if (!leaf) return undefined;
    const segments = [getLabel(leaf), ...parents.map((parent) => getLabel(parent))]
        .filter(Boolean)
        .filter((segment, index, all) => index === 0 || segment !== all[index - 1]);
    return segments.length ? segments.join(' / ') : undefined;
}
